import React from 'react'
import { useParams } from 'react-router-dom'
import { Layout } from 'antd'
import useFetch from 'use-http'
import styled from 'styled-components'
import { GeneralContainer } from '../layouts/components/GeneralContainer'
import { DefaultLayout } from '../layouts/DefaultLayout'
import { ChartData, transformRawIndicatorData, useGetIndicators } from '../config/indicators-model'
import { MainMenu } from '../layouts/components/MainMenu'
import { DBVisor } from '../layouts/components/DBVisor'
import { Chart } from '../components/Chart'
import { ErrorState } from '../components/ErrorState'
import { LoadingState } from '../components/LoadingState'
import { useDelayedLoading } from '../hooks/useDelayedLoading'
import { getAbsoluteServerUrl } from '../config/config'

const NO_ENCONTRADO = {
  title: 'No hemos encontrado esta página',
  message: 'Puede que el enlace esté mal o que este indicador ya no esté disponible.',
}

export const Indicators: React.FC = () => {
  const { categorySlug, subCategorySlug, indicatorSlug } = useParams<{ categorySlug: string; subCategorySlug: string, indicatorSlug: string }>()

  const fetchUrl = getAbsoluteServerUrl(`/indicators/${categorySlug}/${subCategorySlug}/${indicatorSlug}`)
  const dataRequest = useFetch<ChartData>(fetchUrl, { responseType: 'json' }, [fetchUrl])
  const chartData = dataRequest.data ? transformRawIndicatorData(dataRequest.data) : undefined

  const indicatorsRequest = useGetIndicators()
  const indicators = indicatorsRequest.data || []

  const cargando = dataRequest.loading || indicatorsRequest.loading
  // el aviso de carga espera un poco: si la peticion contesta enseguida, no parpadea
  const mostrarCarga = useDelayedLoading(cargando)

  // mientras las peticiones estan en vuelo la pagina no puede saber todavia si el
  // indicador existe: se enseña la estructura (menu y visor) pero sin avisos, y el
  // spinner solo cuando la espera se alarga
  if (cargando) {
    return (
      <CustomLayout>
        <MainMenu />
        <DBVisor pageTitle="Base de datos" />
        <DefaultLayout currentPage={`/bdd/${categorySlug}/${subCategorySlug}/${indicatorSlug}`}>
          <GeneralContainer>{mostrarCarga && <LoadingState fullPage={false} />}</GeneralContainer>
        </DefaultLayout>
      </CustomLayout>
    )
  }

  // el indicador de la URL no existe: el backend contesta 404, y eso no es un
  // fallo suyo, es que la direccion esta mal
  if (dataRequest.response?.status === 404) {
    return <ErrorState {...NO_ENCONTRADO} />
  }

  // el backend no ha dado los datos (429, 500, red...) o no hay catalogo
  if (dataRequest.error || indicatorsRequest.error || !indicators.length) {
    return (
      <ErrorState
        title="No hemos podido cargar esta página"
        message="Ha fallado la conexión con el servidor. Vuelve a intentarlo en unos segundos."
      />
    )
  }

  // respuesta sin series: el indicador de la URL no existe
  const subChart = chartData ? Object.keys(chartData)[0] : undefined

  if (!subChart) {
    return <ErrorState {...NO_ENCONTRADO} />
  }

  const chart = subChart.substr(0, 2)

  const selectedChart = indicators.find(c => c.code === chart)
  const selectedSubChart = selectedChart?.indicators.find(sc => sc.code === subChart)

  // una URL que no cuadre con ningun indicador ya no revienta la aplicacion
  if (!selectedChart || !selectedSubChart) {
    return <ErrorState {...NO_ENCONTRADO} />
  }

  return (
    <CustomLayout>
      <MainMenu />
      <DBVisor pageTitle="Base de datos" />
      <DefaultLayout
        currentPage={`/bdd/${categorySlug}/${subCategorySlug}/${indicatorSlug}`}
        title={`${selectedChart.title} - ${selectedSubChart.title}`}>
        <GeneralContainer>
          <Chart chart={selectedChart.code} subChart={selectedSubChart.code}/>
        </GeneralContainer>
      </DefaultLayout>
    </CustomLayout>
  )
}

const CustomLayout = styled(Layout)`
  /* el visor ya reserva la altura de la cabecera: sin padding extra arriba */
  padding-top: 0;
  background-color: #fff;
`
